import { useCallback } from "react";
import instance from "@network/api";
import { useDispatch, useSelector } from "react-redux";
import { setTheme } from "@store/themeReducer";
import { RootState } from "@store/store";

type ThemePayload = {
  [key: string]: string | undefined;
  backgroundColorType?: string;
  backgroundFromColor?: string;
  backgroundToColor?: string;
  logoUrl?: string;
  logoImage1?: string;
  logoImage2?: string;
  logoImage3?: string;
  logoImage4?: string;
};

const THEME_CSS_VARS: { [key: string]: string } = {
  primaryColor: "--primary-color",
  secondaryColor: "--secondary-color",
  primaryButtonColor: "--primary-button-color",
  primaryButtonLabelColor: "--primary-button-label-color",
  backgroundColor: "--background-color",
  tertiaryColor: "--tertiary-color",
  containerTwoBg: "--container-two-bg",
  gradientColor: "--gradient-color",
  iconColors: "--icon-colors",
  errorColor: "--error-color",
  successColor: "--success-color",
  infoColor: "--info-color",
  placeholderColor: "--placeholder-color",
  inputBorderColor: "--input-border-color",
  inputTextColor: "--input-text-color",
  inputBackgroundColor: "--input-background-color",
  focusColor: "--focus-color",
  hoverColor: "--hover-color",
  disabledButtonBorderColor: "--disabled-button-border-color",
  disabledButtonLabelColor: "--disabled-button-label-color",
  primaryLabelColor: "--primary-label-color",
  secondaryLabelColor: "--secondary-label-color",
  tertiaryLabelColor: "--tertiary-label-color",
  subtitleColor: "--subtitle-color",
  inputLabelColor: "--input-label-color",
  inputPlaceholderColor: "--input-placeholder-color",
  iconBgColor: "--icon-bg-color",
  activeTextColor: "--active-text-color",
  activeIconColor: "--active-icon-color",
  activeIconBgColor: "--active-icon-bg-color",
  raffleBg: "--raffle-bg-color",
};

const useThemeInitializer = () => {
  const urlObj = new URL(window.location.href);
  const isDabur = urlObj.origin === process.env.REACT_APP_DABUR_WNNR;

  const dispatch = useDispatch();
  const theme = useSelector((state: RootState) => state.theme);

  const applyThemeCssVars = useCallback((payload: ThemePayload, themeName: string) => {
    document.body.setAttribute("data-theme", themeName);

    let css = `[data-theme="${themeName}"] {\n`;
    Object.entries(THEME_CSS_VARS).forEach(([key, cssVar]) => {
      if (payload[key]) {
        css += `  ${cssVar}: ${payload[key]};\n`;
      }
    });

    if (
      payload.backgroundColorType === "gradient" &&
      payload.backgroundFromColor &&
      payload.backgroundToColor
    ) {
      const gradient = `linear-gradient(341.1deg, ${payload.backgroundFromColor} -28.66%, ${payload.backgroundToColor} 87.23%)`;
      css += `  --background-color: ${gradient};\n`;
    }

    if (payload.heroColorType === "image" && payload.heroBgImage && payload.heroBgImage !== undefined) {
      css += `--hero-bg-image: url(${payload.heroBgImage});\n`;
    }
    if (payload.heroColorType === "gradient" && payload.heroBackgroundFromColor && payload.heroBackgroundToColor) {
      css += `--tertiary: linear-gradient(341.1deg, ${payload.heroBackgroundFromColor} -28.66%, ${payload.heroBackgroundToColor} 87.23%);\n`;
      css += `--hero-bg-image: none;\n`;
    }
    css += `  --background-image: none;\n`;
    css += `  --header-logo-size: 50px;\n`;
    css += `  --footer-logo-size: 60px;\n`;
    css += "}";

    const styleId = `theme-style-${themeName}`;
    let styleEl = document.getElementById(styleId) as HTMLStyleElement;
    if (!styleEl) {
      styleEl = document.createElement("style");
      styleEl.id = styleId;
      document.head.appendChild(styleEl);
    }
    styleEl.innerHTML = css;

    if (payload.logoUrl) {
      const logoUrlImg = document.querySelector('img[alt="logo"]') as HTMLImageElement | null;
      if (logoUrlImg) {
        logoUrlImg.src = payload.logoUrl;
      }
    }
  }, []);

  const fetchTheme = async (page: string) => {
    try {
      const res = await instance.post("/admin/custom-page/domain", { domain: urlObj.origin, page });
      if (res?.data?.code === 200) {
        const { theme: themeData } = res.data.data;
        applyThemeCssVars(themeData, page);
        // dispatch(setTheme({
        //   logoUrl: themeData.logoUrl,
        //   logoImage1: themeData.logoImage1,
        //   logoImage2: themeData.logoImage2,
        //   logoImage3: themeData.logoImage3,
        //   logoImage4: themeData.logoImage4,
        //   name: res.data.data.name
        // }));
        // document.title = `${res.data.data.name}  WNNR`;
        // const favicon = document.querySelector("link[rel~='icon']") as HTMLLinkElement | null;
        // if (favicon) favicon.href = themeData.logoUrl;
      }
    } catch (error) {
      console.error("Failed to fetch theme config:", error);
    }
  }


  return { isDabur, theme, fetchTheme };
};

export default useThemeInitializer;